/**
 * DocumentRow — one document in the `MasterListCard` list, mirroring the
 * customer-row grid/avatar/rail language. Responsive: avatar + name on mobile,
 * adding type / size / uploaded columns at md/lg. Hover reveals a neutral rail
 * + download/delete actions.
 */
import { Download, Trash2 } from "lucide-react";
import { cn } from "@flint/ui";
import { railClass } from "../../master-list/index.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { formatBytes, fileKind } from "../lib/format.ts";
import { DocAvatar } from "./doc-avatar.tsx";
import { KbStatusPill, KbToggleButton, type KbStatus } from "./kb-status.tsx";

export interface DocumentRowItem {
  id: string;
  title: string;
  originalName: string;
  contentType: string;
  sizeBytes: string;
  tags: string[];
  isKb: boolean;
  kbStatus: KbStatus;
  uploadedAt: string;
}

export interface DocumentRowProps {
  document: DocumentRowItem;
  onDownload: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleKb: (id: string, enabled: boolean) => void;
}

export function DocumentRow({ document: d, onDownload, onDelete, onToggleKb }: DocumentRowProps) {
  return (
    <div
      className={cn(
        "group relative grid items-center gap-3 px-4 py-3.5 transition-colors hover:bg-raised md:gap-4 md:px-5",
        "grid-cols-[44px_minmax(0,1fr)_auto]",
        "md:grid-cols-[44px_minmax(0,1.6fr)_minmax(0,1fr)_96px_120px_auto]",
      )}
    >
      <span aria-hidden className={railClass("none")} />

      <DocAvatar contentType={d.contentType} size="md" />

      {/* Name + original filename */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-base font-bold tracking-tight text-ink">{d.title}</p>
          {d.isKb && <KbStatusPill status={d.kbStatus} />}
        </div>
        {d.originalName !== d.title && (
          <p className="truncate text-xs text-muted">{d.originalName}</p>
        )}
      </div>

      {/* Tags (lg+) */}
      <div className="hidden min-w-0 md:block">
        <div className="flex flex-wrap gap-1">
          {d.tags.slice(0, 4).map((t) => (
            <span key={t} className="rounded-sm border border-line bg-bg px-1.5 py-px text-xs text-ink-2">
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Type + size (md+) */}
      <div className="hidden flex-col gap-0.5 text-right md:flex">
        <span className="text-sm font-semibold text-ink-2">{fileKind(d.contentType)}</span>
        <span className="text-xs tabular-nums text-muted">{formatBytes(d.sizeBytes)}</span>
      </div>

      {/* Uploaded (md+) */}
      <div className="hidden text-right md:block">
        <span className="text-xs tabular-nums text-muted">{formatDateTime(d.uploadedAt)}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1">
        <KbToggleButton isKb={d.isKb} onToggle={(enabled) => onToggleKb(d.id, enabled)} />
        <button
          type="button"
          onClick={() => onDownload(d.id)}
          title="Download"
          className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-line bg-paper text-muted hover:bg-bg hover:text-ink"
        >
          <Download size={14} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => onDelete(d.id)}
          title="Delete"
          className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-line bg-paper text-muted hover:border-bad-100 hover:bg-bad-50 hover:text-bad"
        >
          <Trash2 size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}
